/*  utils/razorpay.js  --------------------------------------------------- */
/*  Zero-dependency Razorpay helper for any React / Next page              */
/*  1) lazy-loads checkout.js exactly once                                 */
/*  2) opens the widget for an already-created order                       */
/*  3) lets the caller decide what to do on success / failure              */

import { isModernWebView } from '@/android/bridge';
import { validateRazorpayOrder } from '../services/razorpayApi';

/* ---------- 1. load the SDK once per tab ---------- */
let sdkPromise = null;
export const loadRazorpaySdk = () => {
  if (typeof window === 'undefined') return Promise.reject('SSR');
  if (window.Razorpay) return Promise.resolve();

  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  return sdkPromise;
};

/* ---------- 2. open checkout for an existing order ---------- */
export const openRazorpay = async ({
  order,
  customer = {},              // { name, phone }
  onSuccess = () => {},
  onFailure = () => {},
}) => {
  /* NEW: explicit SDK-load error handling */          
  try {
    await loadRazorpaySdk();
  } catch (err) {
    console.error('Razorpay SDK failed to load', err); 
    onFailure(err);                                     
    return;                                             
  }

  const opts = {
    key        : process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
    name       : 'Behtar Technologies Pvt Ltd',
    description: 'Payment for Jobs Portal',
    currency   : 'INR',
    image      : 'https://play-lh.googleusercontent.com/dumEGlKKiXHU5Z71_MuZBFTAD8GNo3WV6igA0h4wGHeUbIrD9aavWZ7IIGMBEcGJ49Q',

    order_id: order?.id,
    amount   : order?.amount,           // paise
    prefill  : { name: customer?.name, contact: customer?.phone },

    handler: async (resp) => {
      try {
        await validateRazorpayOrder({
          paymentId : resp?.razorpay_payment_id,
          orderRowId: order?.order_row_id,
          signature : resp?.razorpay_signature,
        });
        onSuccess(resp);
      } catch (err) {
        onFailure(err);
      }
    },
    ...(isModernWebView() && { webview_intent: true })
  };

  const rzp = new window.Razorpay(opts);
 
  rzp.on('payment.failed', async (err) => {           
    try {
      const paymentId = err?.error?.metadata?.payment_id; 
      await validateRazorpayOrder({                         
        paymentId, orderRowId: order?.order_row_id, signature: '' });
    } catch (ve) {
      console.warn('Validate-on-fail error (ignored):', ve); 
    } finally {
      onFailure(err);
    }
  });
  rzp.open();
};

const isVisible = (el) => {
  if (!el) return false;
  const cs = getComputedStyle(el);
  return cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
};

export const isRazorpayOpen = () => {
  if (typeof document === 'undefined') return false;

  /*  overlay div */
  const overlay = document.querySelector('.razorpay-container');
  if (isVisible(overlay)) return true;

  /*  any checkout iframe that is still on screen */
  const activeFrame = Array.from(
    document.querySelectorAll('iframe[id^="razorpay-checkout-frame"]')
  ).find(isVisible);

  return Boolean(activeFrame);
};

export const openRazorpayRedirect = async ({
  order,           // createRazorpayOrder() response
  customer = {},   // { name, phone }
}) => {
  await loadRazorpaySdk();

  /* callback goes to your backend first, then it will 302 back to /hire/post-job */
  const callbackUrl =
    `https://ads.getlokalapp.com/payments/razorpay/wbv/callback/validate/?id=${order.order_row_id}&source=post-job`;

  const opts = {
    key        : process.env.NEXT_PUBLIC_RAZORPAY_KEY_ID,
    order_id   : order.id,
    amount     : order.amount,
    currency   : 'INR',
    name       : 'Behtar Technologies Pvt Ltd',
    description: 'Payment for Jobs Portal',
    prefill    : { name: customer.name, contact: customer.phone },

    /* -------- hosted-checkout magic -------- */
    redirect   : true,
    callback_url: callbackUrl,
    ...(isModernWebView() && { webview_intent: true })
  };

  new window.Razorpay(opts).open();   // no handler – Razorpay will redirect
};